import { Avatar } from "@mui/material";
import { useTranslation } from "react-i18next";
import useFetch from "../../hooks/useFetch";
import useHorizontalScroll from "../../hooks/useHorizontalScroll ";
import googleLogo from "../../images/testimonials/google_logo.png";

const PLACE_ID = "ChIJ2XFOmkdA0i0Rmt1g0SGS7Z0";

function Testimonials() {
  const { t } = useTranslation();
  const scrollRef = useHorizontalScroll();

  const { items: reviews, isLoading } = useFetch({
    module: `common/gmaps-review?placeId=${PLACE_ID}`,
    enabled: true,
  });

  return (
    <section className="relative py-20 overflow-hidden md:py-28 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Decorative background elements */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-20 right-20 w-96 h-96 bg-[#f5272b] rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 left-20 w-96 h-96 bg-[#f5272b] rounded-full blur-3xl"></div>
      </div>

      <div className="container relative z-10 px-4 mx-auto md:px-8">
        <div className="mx-auto max-w-7xl">
          {/* Header Section */}
          <div className="mb-16 space-y-4 text-center">
            <h4 className="text-[#f5272b] font-semibold text-lg md:text-xl tracking-wide uppercase">
              {t("review_by_people")}
            </h4>
            <h2 className="text-4xl font-bold leading-tight text-white md:text-5xl lg:text-6xl">
              {t("client_testimonial")}
            </h2>
            <p className="max-w-3xl mx-auto text-lg leading-relaxed md:text-xl text-slate-300">
              {t("client_testimonial_desc")}
            </p>
            <div className="w-24 h-1.5 bg-gradient-to-r from-transparent via-[#f5272b] to-transparent mx-auto rounded-full"></div>
          </div>

          {/* Testimonials Carousel */}
          <div className="relative mb-12">
            {/* Scroll indicators */}
            <div className="absolute top-0 bottom-0 left-0 z-10 hidden w-20 pointer-events-none bg-gradient-to-r from-slate-900 to-transparent md:block"></div>
            <div className="absolute top-0 bottom-0 right-0 z-10 hidden w-20 pointer-events-none bg-gradient-to-l from-slate-900 to-transparent md:block"></div>

            <div
              ref={scrollRef}
              className="flex gap-6 pb-6 overflow-x-auto select-none scroll-smooth snap-x snap-mandatory"
              style={{
                WebkitOverflowScrolling: "touch",
                scrollbarWidth: "none",
                msOverflowStyle: "none",
              }}
            >
              {isLoading ? (
                <div className="flex items-center justify-center w-full py-20">
                  <div className="relative">
                    <div className="w-16 h-16 border-4 border-slate-700 border-t-[#f5272b] rounded-full animate-spin"></div>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <i className="fa-solid fa-comments text-[#f5272b] text-2xl"></i>
                    </div>
                  </div>
                </div>
              ) : reviews?.reviews?.length > 0 ? (
                reviews.reviews.map((review, index) => {
                  const fullStars = Math.floor(review.rating);
                  const hasHalfStar = review.rating % 1 >= 0.5;

                  return (
                    <div key={index} className="flex-shrink-0 w-full md:w-[500px] snap-start">
                      <div className="group h-full bg-gradient-to-br from-slate-800/90 to-slate-900/90 backdrop-blur-sm rounded-3xl p-8 shadow-xl border border-slate-700/50 hover:border-[#f5272b]/50 transition-all duration-300">
                        <div className="flex flex-col h-full">
                          {/* Rating Header */}
                          <div className="flex items-center justify-between mb-6">
                            <div className="flex items-center gap-3">
                              <div className="flex items-center justify-center w-12 h-12 bg-[#f5272b]/10 rounded-xl">
                                <span className="text-2xl font-bold text-[#f5272b]">{review.rating}</span>
                              </div>
                              <div className="flex items-center gap-1">
                                {Array.from({ length: fullStars }).map((_, i) => (
                                  <i key={i} className="text-lg text-yellow-400 fa-solid fa-star"></i>
                                ))}
                                {hasHalfStar && <i className="text-lg text-yellow-400 fa-solid fa-star-half-alt"></i>}
                              </div>
                            </div>
                            <div className="w-10 h-10 bg-white rounded-lg p-1.5 shadow-lg flex items-center justify-center">
                              <img src={googleLogo} alt="Google" className="object-contain w-full h-full" />
                            </div>
                          </div>

                          {/* Review Text */}
                          <div className="flex-1 mb-6">
                            <div className="relative">
                              <i className="fa-solid fa-quote-left text-[#f5272b]/20 text-4xl absolute -top-2 -left-2"></i>
                              <p className="pl-6 text-base leading-relaxed text-slate-300 line-clamp-6">
                                {review.text}
                              </p>
                            </div>
                          </div>

                          {/* Author Info */}
                          <div className="flex items-center gap-4 pt-6 border-t border-slate-700/50">
                            <div className="relative">
                              <div className="absolute inset-0 bg-[#f5272b]/20 rounded-full blur-lg"></div>
                              <Avatar
                                src={review.profile_photo_url}
                                alt={review.author_name}
                                sx={{
                                  width: 56,
                                  height: 56,
                                  border: "2px solid rgba(245, 39, 43, 0.3)",
                                }}
                              />
                            </div>
                            <div className="flex-1 min-w-0">
                              <h4 className="text-lg font-bold text-white truncate">{review.author_name}</h4>
                              <p className="flex items-center gap-2 text-sm text-slate-400">
                                <i className="fa-regular fa-clock"></i>
                                {review.relative_time_description}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="w-full p-16 text-center border bg-gradient-to-br from-slate-800/90 to-slate-900/90 backdrop-blur-sm rounded-3xl border-slate-700/50">
                  <div className="flex items-center justify-center w-20 h-20 mx-auto mb-6 rounded-full bg-slate-700/50">
                    <i className="text-4xl fa-regular fa-comment-dots text-slate-400"></i>
                  </div>
                  <p className="text-lg text-slate-400">No reviews available.</p>
                </div>
              )}
            </div>
          </div>

          {/* Footer Summary */}
          <div className="p-8 border shadow-xl bg-gradient-to-r from-slate-800/90 to-slate-900/90 backdrop-blur-sm rounded-3xl md:p-10 border-slate-700/50">
            <div className="flex flex-col items-center justify-between gap-6 md:flex-row">
              {/* Google Logo */}
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 p-3 bg-white shadow-lg rounded-2xl">
                  <img src={googleLogo} alt="Google" className="object-contain w-full h-full" />
                </div>
                <div>
                  <p className="text-xl font-bold text-white">Google Reviews</p>
                  <p className="text-sm text-slate-400">Verified Reviews</p>
                </div>
              </div>

              {/* Rating Display */}
              <div className="flex flex-col items-center gap-6 md:flex-row">
                <div className="flex items-center gap-4">
                  <div className="text-center">
                    <div className="mb-1 text-5xl font-bold text-white">{reviews?.rating?.toFixed(1) || "0.0"}</div>
                    <div className="flex items-center justify-center gap-1">
                      {Array.from({ length: Math.floor(reviews?.rating || 0) }).map((_, i) => (
                        <i key={i} className="text-lg text-yellow-400 fa-solid fa-star"></i>
                      ))}
                      {reviews?.rating % 1 >= 0.5 && (
                        <i className="text-lg text-yellow-400 fa-solid fa-star-half-alt"></i>
                      )}
                    </div>
                  </div>
                </div>

                <div className="hidden w-px h-12 bg-slate-700 md:block"></div>

                {/* Total Reviews */}
                <div className="text-center md:text-left">
                  <div className="mb-1 text-3xl font-bold text-white">
                    {reviews?.user_ratings_total?.toLocaleString() || "0"}
                  </div>
                  <p className="text-sm text-slate-400">Total Reviews</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        .line-clamp-6 {
          display: -webkit-box;
          -webkit-line-clamp: 6;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>
    </section>
  );
}

export default Testimonials;
